<template>
    <div class="email-editor">
        <email-editor-top-bar
            :subject="props.subject"
            @save="editor.save()"
            @back="emit('back')"
            @send-test="editor.send"
            @update-subject="emit('updateSubject', $event)"
            @toggle-preview="editor.togglePreview()"
            @toggle-comments="editor.toggleComments()"
        />

        <div id="bee-container" />
    </div>
</template>

<script setup lang="ts">
    import BeefreeSDK from '@beefree.io/sdk';
    import type {
        IBeeConfig,
        IMergeContent,
        IMergeTag,
        IPluginDisplayCondition,
        ISpecialLink,
        IToken,
    } from '@beefree.io/sdk/dist/types/bee';

    import { type Company, SendTestEmailCommand } from '@keyvue/one-api-client';
    import EmailEditorTopBar from '@/flow/flow/components/email-editor/EmailEditorTopBar.vue';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import useNotify from '@/services/keyvue-ui/composables/useNotify';

    export interface BeefreeSaveEvent {
        pageJson: string;
        pageHtml: string;
        ampHtml: string | null;
        templateVersion: number;
        language: string | null;
    }

    interface Props {
        token: string
        template: string
        subject: string
        company: Company
        mergeTags?: IMergeTag[]
        mergeContent?: IMergeContent[]
        specialLinks?: ISpecialLink[],
        displayConditions?: IPluginDisplayCondition[]
    }

    const props = withDefaults(defineProps<Props>(), {
        mergeTags: () => [],
        mergeContent: () => [],
        specialLinks: () => [],
        displayConditions: () => [],
    });

    const emit = defineEmits<{
        (e: 'save', payload: BeefreeSaveEvent): void;
        (e: 'back'): void;
        (e: 'updateSubject', changedSubject: string): void;
    }>();

    const beeConfig: IBeeConfig = {
        uid: props.company.id.toString(),
        container: 'bee-container',
        language: 'nl-NL',
        commenting: false,
        mergeTags: props.mergeTags,
        mergeContent: props.mergeContent,
        specialLinks: props.specialLinks,
        rowDisplayConditions: props.displayConditions,
        contentDefaults: {
            general: {
                contentAreaWidth: '600px',
            },
        },
        onSend: sendTest,
        onSave(pageJson, pageHtml, ampHtml, templateVersion, language) {
            emit('save', {
                pageJson,
                pageHtml,
                ampHtml,
                templateVersion,
                language,
            });
        },
    // rowDisplayConditions is not in the type definition
    } as unknown as IBeeConfig;

    const editor = new BeefreeSDK({
        access_token: props.token,
        v2: true,
    } as IToken);

    editor.start(beeConfig, JSON.parse(props.template));

    const { client, currentUser } = useClient();
    const { notifySaved, notifyError } = useNotify();

    async function sendTest(html: string) {
        if (!currentUser.value?.email) {
            return;
        }

        try {
            await client.dispatch(new SendTestEmailCommand(
                currentUser.value.email,
                props.subject,
                html,
            ));

            notifySaved(`Er is een test e-mail verstuurd naar ${currentUser.value.email}`, '', 3000);
        } catch (error) {
            notifyError(error);
        }
    }
</script>

<style scoped lang="scss">
.email-editor {
    display: grid;
    height: 100vh;
    grid-template-rows: min-content auto;
}
</style>
